import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import Seo from '@/components/Seo';
import { toast, Toaster } from 'sonner';
import { Wand2, Upload, Download, Loader2, Lock, Save, Sparkles, RefreshCw } from 'lucide-react';

const PRESETS = [
  'Indian sandstone patio', 'Porcelain paving', 'Block paving driveway', 'Resin-bound driveway',
  'Gravel driveway', 'Artificial lawn', 'New turf lawn', 'Composite decking',
  'Raised sleeper beds', 'Modern minimalist design', 'Cottage garden planting', 'Fresh fencing & borders',
];

async function fileToDataUrl(file) {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
}
// Downscale big phone photos in the browser so any size uploads fine and the AI runs faster.
async function downscaleImage(file, maxDim = 2048, quality = 0.9) {
  let bmp;
  try { bmp = await createImageBitmap(file, { imageOrientation: 'from-image' }); }
  catch { bmp = await createImageBitmap(file); }
  let width = bmp.width, height = bmp.height;
  const scale = Math.min(1, maxDim / Math.max(width, height));
  width = Math.round(width * scale); height = Math.round(height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  canvas.getContext('2d').drawImage(bmp, 0, 0, width, height);
  const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality));
  return blob || file;
}
function dataUrlToBlob(dataUrl) {
  const [meta, b64] = dataUrl.split(',');
  const mime = meta.match(/:(.*?);/)[1];
  const bin = atob(b64); const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

export default function DesignVisualiserPage() {
  const { user } = useAuth();
  const isPro = user?.is_pro || user?.role === 'admin';
  const [file, setFile] = useState(null);
  const [beforeUrl, setBeforeUrl] = useState('');
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [saving, setSaving] = useState(false);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Please choose an image'); return; }
    setResult('');
    try {
      const small = await downscaleImage(f);
      setFile(small);
      setBeforeUrl(await fileToDataUrl(small));
    } catch { toast.error('Could not read that image — try another'); }
    e.target.value = '';
  };

  const addPreset = (p) => setPrompt((prev) => (prev ? `${prev}, ${p.toLowerCase()}` : p));

  const generate = async () => {
    if (!file) { toast.error("Upload the client's photo first"); return; }
    if (!prompt.trim()) { toast.error('Describe the design you want'); return; }
    setLoading(true); setResult('');
    try {
      const fd = new FormData();
      fd.append('file', file, 'photo.jpg'); fd.append('prompt', prompt.trim());
      const { data } = await api.post('/design/visualise', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      setResult(data.image);
      toast.success('Design created!');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create the design — try again');
    } finally { setLoading(false); }
  };

  const download = () => {
    const a = document.createElement('a');
    a.href = result; a.download = `design-${Date.now()}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const saveToGallery = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', dataUrlToBlob(result), `design-${Date.now()}.png`);
      fd.append('caption', prompt.trim());
      fd.append('kind', 'design');
      await api.post('/gallery', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Saved to your gallery');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };

  if (!isPro) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center" data-testid="design-locked">
        <Lock className="mx-auto text-brand mb-4" size={40} />
        <h1 className="font-display font-semibold text-3xl uppercase tracking-tight">A Pro feature</h1>
        <p className="text-slate-600 mt-2">Turn a client's photo into a realistic redesign to help them picture the finished job. Start your 14-day free trial to unlock it.</p>
        <a href="/pricing" className="tm-btn inline-flex mt-5" data-testid="design-upgrade-btn">See Pro plans</a>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="design-visualiser-page">
      <Seo title="AI Design Visualiser" noindex />
      <Toaster position="top-center" richColors />

      <header className="mb-6">
        <p className="text-xs uppercase tracking-widest text-brand font-semibold">Business · Design</p>
        <h1 className="font-display font-semibold text-3xl sm:text-4xl uppercase tracking-tight flex items-center gap-2">
          <Wand2 className="text-brand" size={30} /> AI Design Visualiser
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Upload a photo of the client's garden or driveway, describe what you'd do, and generate a realistic "after" concept to help them picture the finished job.</p>
      </header>

      <div className="grid lg:grid-cols-[360px_1fr] gap-6">
        {/* Controls */}
        <div className="space-y-4">
          <label className="tm-card p-5 text-center cursor-pointer block hover:shadow-lg transition-shadow" data-testid="design-upload-drop">
            <Upload className="mx-auto text-brand" size={24} />
            <p className="font-bold mt-1">{file ? 'Change photo' : "Upload client's photo"}</p>
            <p className="text-[11px] text-slate-500">Garden or driveway — clear daylight shot works best. Any size welcome (we optimise it automatically).</p>
            <input type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="design-upload-input" />
          </label>

          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-semibold">Describe the design</p>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4}
              placeholder="e.g. Indian sandstone patio with a raised sleeper bed on the left, artificial lawn and a small gravel border"
              data-testid="design-prompt" className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-brand outline-none resize-none" />
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="design-presets">
              {PRESETS.map((p) => (
                <button key={p} onClick={() => addPreset(p)} data-testid={`design-preset-${p.replace(/\s+/g, '-').toLowerCase()}`}
                  className="text-[11px] font-mono border border-slate-300 px-2 py-1 hover:border-brand hover:text-brand transition-colors">+ {p}</button>
              ))}
            </div>
          </div>

          <button onClick={generate} disabled={loading} data-testid="design-generate-btn" className="tm-btn w-full justify-center inline-flex disabled:opacity-60">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Designing… (~20s)</> : <><Sparkles size={16} /> Generate design</>}
          </button>
          <p className="text-[11px] text-slate-400">This is an AI concept image to inspire the client — not a measured plan. The accurate to-scale Plan Designer is coming next.</p>
        </div>

        {/* Preview */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="tm-card p-3">
            <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 font-semibold">Before</p>
            {beforeUrl
              ? <img src={beforeUrl} alt="Before" className="w-full aspect-[4/3] object-cover bg-slate-100" data-testid="design-before-img" />
              : <div className="w-full aspect-[4/3] bg-slate-100 flex items-center justify-center text-slate-400 text-sm">Upload a photo</div>}
          </div>
          <div className="tm-card p-3">
            <p className="text-[11px] uppercase tracking-widest text-brand mb-2 font-semibold">After (AI concept)</p>
            {loading
              ? <div className="w-full aspect-[4/3] bg-slate-100 flex flex-col items-center justify-center text-slate-400"><Loader2 className="animate-spin mb-2" /> Creating design…</div>
              : result
                ? <img src={result} alt="After" className="w-full aspect-[4/3] object-cover bg-slate-100" data-testid="design-after-img" />
                : <div className="w-full aspect-[4/3] bg-slate-100 flex items-center justify-center text-slate-400 text-sm text-center px-4">Your design will appear here</div>}
            {result && !loading && (
              <div className="flex gap-2 mt-3" data-testid="design-actions">
                <button onClick={download} data-testid="design-download-btn" className="flex-1 inline-flex items-center justify-center gap-1 text-xs uppercase border border-slate-800 py-1.5 hover:bg-slate-900 hover:text-white transition-colors font-semibold"><Download size={13} /> Download</button>
                <button onClick={saveToGallery} disabled={saving} data-testid="design-save-btn" className="flex-1 inline-flex items-center justify-center gap-1 text-xs uppercase border border-slate-200 py-1.5 hover:bg-brand hover:text-white hover:border-brand transition-colors disabled:opacity-60 font-semibold">{saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save to gallery</button>
                <button onClick={generate} data-testid="design-regen-btn" title="Try again" className="inline-flex items-center border border-slate-300 py-1.5 px-2 text-slate-500 hover:border-brand hover:text-brand"><RefreshCw size={13} /></button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 tm-card p-4 flex items-center gap-3 flex-wrap">
        <p className="text-sm text-slate-600 flex-1">Turn the before &amp; after into a shareable post or add it to your portfolio.</p>
        <Link to="/before-after" className="tm-btn tm-btn-dark inline-flex text-sm">Before &amp; After maker</Link>
        <Link to="/gallery" className="tm-btn inline-flex text-sm">My gallery</Link>
      </div>
    </div>
  );
}
